import { Color, TextStyle } from "@tiptap/extension-text-style";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, RemoveFormatting } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Text colors an admin can apply to a selection. "Default" clears the mark so
 * the text follows the theme again.
 */
const TEXT_COLORS = [
  { value: "#b91c1c", label: "Red" },
  { value: "#b45309", label: "Amber" },
  { value: "#15803d", label: "Green" },
  { value: "#1d4ed8", label: "Blue" },
  { value: "#7e22ce", label: "Purple" },
] as const;

/**
 * Minimal rich-text editor for announcement messages: bold, italic, and text
 * color. Emits HTML ("" when empty); the dashboard renders it through the
 * sanitizer in `AnnouncementBody`.
 */
export function AnnouncementBodyEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        link: false,
      }),
      TextStyle,
      Color,
    ],
    content: value,
    editorProps: {
      attributes: {
        class:
          "min-h-[96px] max-h-[240px] overflow-y-auto px-3 py-2 text-sm outline-none [&_p]:my-0.5",
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.isEmpty ? "" : e.getHTML());
    },
  });

  if (!editor) return null;

  const toolButton = (active: boolean) =>
    cn(
      "flex size-7 items-center justify-center rounded-md transition-colors",
      active ? "bg-accent text-foreground" : "text-muted-foreground hover:text-foreground",
    );

  return (
    <div className="rounded-md border border-input bg-transparent shadow-xs focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]">
      <div className="flex items-center gap-1 border-b border-input px-2 py-1.5">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={toolButton(editor.isActive("bold"))}
          title="Bold"
          aria-label="Bold"
          aria-pressed={editor.isActive("bold")}
        >
          <Bold className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={toolButton(editor.isActive("italic"))}
          title="Italic"
          aria-label="Italic"
          aria-pressed={editor.isActive("italic")}
        >
          <Italic className="size-3.5" />
        </button>
        <span className="mx-1 h-4 w-px bg-border" />
        {TEXT_COLORS.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => editor.chain().focus().setColor(c.value).run()}
            title={`${c.label} text`}
            aria-label={`${c.label} text`}
            className={cn(
              "flex size-7 items-center justify-center rounded-md transition-colors hover:bg-muted",
              editor.isActive("textStyle", { color: c.value }) && "bg-accent",
            )}
          >
            <span className="size-3.5 rounded-full" style={{ backgroundColor: c.value }} />
          </button>
        ))}
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetColor().unsetAllMarks().run()}
          className={toolButton(false)}
          title="Clear formatting"
          aria-label="Clear formatting"
        >
          <RemoveFormatting className="size-3.5" />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
